<template>
  <v-card-account-settings
    :title="title"
    data-pw="disableEnableUserDialogCard"
  >
    <v-card-text>
      <danger-confirmation-field
        v-model="confirmDanger"
        :message="confirmationMessage"
        :confirm-string="confirmString"
        :action-label="$t('components.DisableEnableDialog.action_label')"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-btn-secondary
        data-pw="cancelDisableEnableButton"
        @click="$emit('close')"
      >
        {{ $t("buttons.cancel") }}
      </v-btn-secondary>
      <v-btn-delete
        @click="disableEnableUser"
        data-pw="confirmDisableEnableButton"
        :disabled="!confirmDanger"
      >
        {{ $t("buttons.confirm") }}
      </v-btn-delete>
    </v-card-actions>
  </v-card-account-settings>
</template>

<script setup lang="ts">
import {computed, PropType, ref, Ref} from "vue";
import {isYapiError, DashUser, YapiError} from "@YenzaCT/sdk";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";

import {useGlobalStore} from "@/store";
import yapi from "@/lib/yapi";
import DangerConfirmationField from "@/components/DangerConfirmationField.vue";

const {t} = useI18n();
const store = useGlobalStore();

const route = useRoute();

const props = defineProps({
  user: {
    type: Object as PropType<DashUser>,
    required: true
  },
});

const confirmString = ref("");
const confirmDanger = ref<boolean>(false);

const userId: Ref<string> = ref("");
userId.value = route.params.userId as string;

const emits = defineEmits<{
  (e: "close"): void;
}
>();

const title = computed(
  () => t(props.user?.auth.accountDisabled ? "components.DisableEnableDialog.enable_user" : "components.DisableEnableDialog.disable_user")
);
const confirmationMessage = computed(
  () => t("components.DisableEnableDialog.warning", { action: props.user?.auth.accountDisabled ? "enable" : "disable" })
);

const disableEnableUser = async () => {
  try {
    store.networkBusy = true;
    await (
      props.user.auth.accountDisabled
        ? yapi.admin.user.enable(userId.value)
        : yapi.admin.user.disable(userId.value)
    );
    emits("close");
    store.showSnackbar(t(props.user?.auth.accountDisabled ? "User Enabled" : "User Disabled"));
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
};

</script>
